import { useEffect, useState } from "react";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import _ from "lodash";

import { transferPublish } from "api/transfer";

import DefaultTable from "components/Table";

import { popupNotification, useLayoutDispatch } from "context/LayoutContext";

import { ExpandMore } from "icons";

import themes from "themes";

import { convertDataToTableView } from "utils/convert";
import { exportToExcel } from "utils/exportToExcel";
import { toTitleCase } from "utils/string";

export default function TransferPublishModal({ onClose, transfers, headers }) {
  const layoutDispatch = useLayoutDispatch();
  const [validTransfers, setValidTransfers] = useState([]);
  const [invalidTransfers, setInvalidTransfers] = useState([]);
  const [publish, setPublish] = useState(false);
  const [expanded, setExpanded] = useState("valid-transfers");

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    const valid_transfers = transfers.filter(
      (t) =>
        t.product.status !== "Inactive" &&
        t.product.status !== "Marked for Deletion" &&
        !t.from_location.is_deleted &&
        !t.to_location.is_deleted &&
        !t.is_published,
    );
    setValidTransfers(valid_transfers);
    setInvalidTransfers(_.xorBy(transfers, valid_transfers, "id"));
  }, [transfers]);

  const handlePublish = () => {
    setPublish(true);
    transferPublish({
      body: {
        transfer_ids: validTransfers.map((vt) => vt.id),
      },
      responseSetter: (res) => {
        setPublish(false);
        const { success, message } = res;
        if (!success) {
          popupNotification({
            dispatch: layoutDispatch,
            message,
          });
          return;
        }

        popupNotification({
          dispatch: layoutDispatch,
          message: "Transfers published.",
          status: "success",
        });
        onClose(true);
      },
    });
  };

  const handleExportData = (transfers) => {
    const today = new Date();
    exportToExcel(
      [],
      `${toTitleCase(expanded)}-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(transfers, [
        {
          id: "id",
        },
        ...headers,
      ]),
    );
  };

  return (
    <Dialog
      open={true}
      onClose={!publish ? () => onClose() : null}
      aria-labelledby="transfer-counts"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle id="transfer-counts">Transfer Publish</DialogTitle>
      <DialogContent>
        {publish ? (
          <Alert severity="info">
            <CircularProgress size={15} /> Please wait until the process is
            complete.
          </Alert>
        ) : (
          <>
            <Accordion
              expanded={expanded === "invalid-transfers"}
              onChange={handleChange("invalid-transfers")}
              style={{
                backgroundColor: invalidTransfers.length
                  ? themes.default.palette.warning.main
                  : "",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="invalid-transfers-content"
                id="invalid-transfers-id"
              >
                <Typography variant="h4">
                  Invalid Transfers ({invalidTransfers.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container>
                  <Grid item lg={12}>
                    <Alert severity="warning">
                      Products in <strong>Inactive</strong>,{" "}
                      <strong>Marked for Deletion</strong> statuses and{" "}
                      <strong>published</strong> transfers cannot be publish.
                      These records will not be included in the transfer
                      request.
                    </Alert>
                  </Grid>
                  <Grid item lg={12}>
                    <DefaultTable
                      data={{
                        items: invalidTransfers,
                        count: invalidTransfers.length,
                        isFetching: false,
                      }}
                      headers={headers}
                      handleExportData={() =>
                        handleExportData(invalidTransfers)
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expanded === "valid-transfers"}
              onChange={handleChange("valid-transfers")}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="valid-transfers-content"
                id="valid-transfers-id"
              >
                <Typography variant="h4">
                  Valid Transfers ({validTransfers.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container>
                  <Grid item lg={12}>
                    <DefaultTable
                      data={{
                        items: validTransfers,
                        count: validTransfers.length,
                        isFetching: false,
                      }}
                      headers={headers}
                      handleExportData={() => handleExportData(validTransfers)}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={!publish ? () => onClose(false) : null}
          color="default"
          disabled={publish}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          color="primary"
          disabled={!validTransfers.length || publish}
        >
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}
